import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Plus, Save, Loader2 } from 'lucide-react';

const OBJECTIVES = [
  { id: 'roas', label: 'ROAS', money: false },
  { id: 'roi', label: 'ROI', money: false },
  { id: 'faturamento', label: 'Faturamento', money: true },
  { id: 'comissao', label: 'Comissão', money: true },
  { id: 'agendamentos', label: 'Agendamentos', money: true },
];
const isMoney = (obj) => (OBJECTIVES.find(o => o.id === obj) || {}).money !== false;

const inp = "w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm";
const inpXs = "w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs";

const TagInput = ({ label, values, onChange, color, placeholder }) => {
  const [val, setVal] = useState('');
  const add = () => { if (val.trim()) { onChange([...(values || []), val.trim()]); setVal(''); } };
  return (
    <div>
      <label className="text-gray-500 text-xs mb-1 block">{label}</label>
      <div className="flex gap-2 mb-2">
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          className={inp} placeholder={placeholder} />
        <button type="button" onClick={add} className="px-3 py-2 bg-blue-600 text-white rounded-lg"><Plus size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-1">
        {(values || []).map((v, i) => (
          <span key={i} className={`flex items-center gap-1 px-2 py-1 ${color} rounded text-sm`}>{v}
            <button onClick={() => onChange(values.filter((_, j) => j !== i))} className="hover:text-red-400"><X size={14} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const SellerFormModal = ({ seller, onClose, onSaved }) => {
  const editing = !!(seller && seller.id);
  const parse = (v) => { try { return typeof v === 'string' ? JSON.parse(v) : (v || []); } catch (e) { return []; } };

  const [form, setForm] = useState({
    name: seller?.name || '',
    sector: seller?.sector || '',
    trackingType: seller?.trackingType || 'utm_medium',
    trackingValues: parse(seller?.trackingValues),
    fixedSalary: parseFloat(seller?.fixedSalary) || 0,
    commissionType: seller?.commissionType || 'commission',
    commissionPercent: parseFloat(seller?.commissionPercent) || 0,
    commissionUpsell: parseFloat(seller?.commissionUpsell) || 0,
    commissionRemarketing: parseFloat(seller?.commissionRemarketing) || 0,
    commissionAfterPay: parseFloat(seller?.commissionAfterPay) || 0,
    goalRoas: parseFloat(seller?.goalRoas) || 0,
    goalRoi: parseFloat(seller?.goalRoi) || 0,
    goals: parse(seller?.goals),
    bonusTiers: parse(seller?.bonusTiers),
    upsellProducts: parse(seller?.upsellProducts),
    remarketingProducts: parse(seller?.remarketingProducts),
    isActive: seller ? !!seller.isActive : true,
  });
  const [sectors, setSectors] = useState([]);
  const [newSector, setNewSector] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/sellers/sectors').then(r => { if (r.data?.success) setSectors(r.data.sectors || []); }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setGoal = (idx, k, v) => { const g = [...form.goals]; g[idx] = { ...g[idx], [k]: v }; set('goals', g); };
  const setTier = (idx, k, v) => { const t = [...form.bonusTiers]; t[idx] = { ...t[idx], [k]: v }; set('bonusTiers', t); };
  const numVal = (obj, raw) => isMoney(obj) ? (parseFloat(raw) || 0) : (parseInt(raw) || 0);

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editing) await api.put(`/sellers/${seller.id}`, form);
      else await api.post('/sellers', form);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{editing ? 'Editar Vendedor' : 'Novo Vendedor'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div><label className="text-gray-400 text-sm mb-1 block">Nome *</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} className={inp} placeholder="Nome do vendedor" /></div>

          {/* Setor (PDF: criacao e selecao) */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Setor</label>
            {!newSector ? (
              <div className="flex gap-2">
                <select value={form.sector || ''} onChange={(e) => set('sector', e.target.value)} className={inp}>
                  <option value="">Sem setor</option>
                  {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                  {form.sector && !sectors.includes(form.sector) && <option value={form.sector}>{form.sector}</option>}
                </select>
                <button type="button" onClick={() => { setNewSector(true); set('sector', ''); }} className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-xs whitespace-nowrap">+ Novo</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={form.sector} onChange={(e) => set('sector', e.target.value)} className={inp} placeholder="Nome do novo setor" autoFocus />
                <button type="button" onClick={() => setNewSector(false)} className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-xs">Listar</button>
              </div>
            )}
          </div>

          {/* Rastreamento */}
          <div><label className="text-gray-400 text-sm mb-1 block">Tipo de rastreamento</label>
            <select value={form.trackingType} onChange={(e) => set('trackingType', e.target.value)} className={inp}>
              <option value="utm_medium">UTM Medium</option><option value="utm_source">UTM Source</option>
              <option value="utm_campaign">UTM Campaign</option><option value="keywords">Palavras-chave</option>
            </select></div>
          <TagInput label="Valores de rastreamento" values={form.trackingValues} onChange={(v) => set('trackingValues', v)}
            color="bg-blue-500/20 text-blue-400" placeholder="Ex: adriano, adr" />

          {/* Remuneracao (PDF: + variavel de upsell) */}
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <h4 className="text-white font-medium text-sm">Remuneração</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-gray-500 text-xs mb-1 block">Salário Fixo</label>
                <input type="number" value={form.fixedSalary} onChange={(e) => set('fixedSalary', parseFloat(e.target.value) || 0)} className={inp} /></div>
              <div><label className="text-gray-500 text-xs mb-1 block">Base da comissão</label>
                <select value={form.commissionType} onChange={(e) => set('commissionType', e.target.value)} className={inp}>
                  <option value="commission">Comissão produtor</option><option value="revenue">Faturamento bruto</option>
                </select></div>
              <div><label className="text-gray-500 text-xs mb-1 block">% Comissão</label>
                <input type="number" value={form.commissionPercent} onChange={(e) => set('commissionPercent', parseFloat(e.target.value) || 0)} className={inp} step="0.1" /></div>
              <div><label className="text-gray-500 text-xs mb-1 block">% Upsell</label>
                <input type="number" value={form.commissionUpsell} onChange={(e) => set('commissionUpsell', parseFloat(e.target.value) || 0)} className={inp} step="0.1" /></div>
              <div><label className="text-gray-500 text-xs mb-1 block">% Remarketing</label>
                <input type="number" value={form.commissionRemarketing} onChange={(e) => set('commissionRemarketing', parseFloat(e.target.value) || 0)} className={inp} step="0.1" /></div>
              <div><label className="text-gray-500 text-xs mb-1 block">% After Pay</label>
                <input type="number" value={form.commissionAfterPay} onChange={(e) => set('commissionAfterPay', parseFloat(e.target.value) || 0)} className={inp} step="0.1" /></div>
            </div>
          </div>

          {/* METAS (PDF: campo separado, objetivo + valor) */}
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <h4 className="text-white font-medium text-sm flex items-center justify-between">Metas
              <button onClick={() => set('goals', [...form.goals, { objective: 'faturamento', value: 0 }])} className="text-blue-400 text-xs hover:text-blue-300">+ Adicionar meta</button>
            </h4>
            {form.goals.length === 0 && <p className="text-gray-500 text-xs">Nenhuma meta definida.</p>}
            {form.goals.map((g, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div><label className="text-gray-500 text-xs">Objetivo</label>
                  <select value={g.objective} onChange={(e) => { setGoal(idx, 'objective', e.target.value); setGoal(idx, 'value', numVal(e.target.value, g.value)); }} className={inpXs}>
                    {OBJECTIVES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select></div>
                <div><label className="text-gray-500 text-xs">{isMoney(g.objective) ? 'Valor (R$)' : 'Valor (inteiro)'}</label>
                  <input type="number" step={isMoney(g.objective) ? '0.01' : '1'} value={g.value}
                    onChange={(e) => setGoal(idx, 'value', numVal(g.objective, e.target.value))} className={inpXs} /></div>
                <button onClick={() => set('goals', form.goals.filter((_, i) => i !== idx))} className="text-red-400 text-xs pb-2 hover:text-red-300">Remover</button>
              </div>
            ))}
          </div>

          {/* BONIFICACOES (PDF: faixas de/ate + R$ fixo ou % sobre objetivo) */}
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <h4 className="text-white font-medium text-sm flex items-center justify-between">Bonificações
              <button onClick={() => set('bonusTiers', [...form.bonusTiers, { objective: 'faturamento', from: 0, to: 0, bonusType: 'fixed', bonusValue: 0 }])} className="text-blue-400 text-xs hover:text-blue-300">+ Adicionar faixa</button>
            </h4>
            {form.bonusTiers.length === 0 && <p className="text-gray-500 text-xs">Nenhuma faixa definida.</p>}
            {form.bonusTiers.map((t, idx) => (
              <div key={idx} className="p-3 bg-gray-800 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs">Faixa {idx + 1}</span>
                  <button onClick={() => set('bonusTiers', form.bonusTiers.filter((_, i) => i !== idx))} className="text-red-400 text-xs hover:text-red-300">Remover</button>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <div><label className="text-gray-500 text-xs">Objetivo</label>
                    <select value={t.objective} onChange={(e) => setTier(idx, 'objective', e.target.value)} className={inpXs}>
                      {OBJECTIVES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select></div>
                  <div><label className="text-gray-500 text-xs">De</label>
                    <input type="number" value={t.from} onChange={(e) => setTier(idx, 'from', numVal(t.objective, e.target.value))} className={inpXs} /></div>
                  <div><label className="text-gray-500 text-xs">Até</label>
                    <input type="number" value={t.to} onChange={(e) => setTier(idx, 'to', numVal(t.objective, e.target.value))} className={inpXs} /></div>
                  <div><label className="text-gray-500 text-xs">Bônus em</label>
                    <select value={t.bonusType} onChange={(e) => setTier(idx, 'bonusType', e.target.value)} className={inpXs}>
                      <option value="fixed">R$ fixo</option><option value="percent">% objetivo</option>
                    </select></div>
                  <div><label className="text-gray-500 text-xs">{t.bonusType === 'percent' ? '%' : 'R$'}</label>
                    <input type="number" step="0.01" value={t.bonusValue} onChange={(e) => setTier(idx, 'bonusValue', parseFloat(e.target.value) || 0)} className={inpXs} /></div>
                </div>
              </div>
            ))}
          </div>

          {/* UPSELL e REMARKETING separados (PDF) */}
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <h4 className="text-white font-medium text-sm">Upsell</h4>
            <TagInput label="Produtos de Upsell" values={form.upsellProducts} onChange={(v) => set('upsellProducts', v)}
              color="bg-purple-500/20 text-purple-400" placeholder="Nome do plano ou código do produto" />
          </div>
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <h4 className="text-white font-medium text-sm">Remarketing</h4>
            <TagInput label="Produtos de Remarketing" values={form.remarketingProducts} onChange={(v) => set('remarketingProducts', v)}
              color="bg-orange-500/20 text-orange-400" placeholder="Nome do plano ou código do produto" />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>
        <div className="p-6 border-t border-gray-700 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-gray-700 text-white rounded-lg">Cancelar</button>
          <button onClick={save} disabled={saving || !form.name} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}{editing ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerFormModal;
